import { useEffect, useMemo, useState } from 'react'
import { Radio } from 'lucide-react'
import { EmptyState } from '../cc/EmptyState'
import { Panel } from '../cc/Panel'
import { EYEBROW } from '../cc/tokens'
import { fetchBusEta, fetchBusRouteDetail, type BusRouteEta } from './useBusApi'
import { STATUS_RESOLVED, nextBusRouteStop, resolveVehicle } from './nagpurRouteResolve'
import type { BusBoardRow, BusRouteDetail, BusRouteStop, BusRouteSummary } from './types'

/** Same 45s cache TTL as mbmt.google_route_eta's own _ETA_TTL_S -- polling faster than
 *  the server's own cache bucket just repeats a stale response. */
const ETA_POLL_MS = 45_000

/** Direct, honest labels for every non-RESOLVED status -- see
 *  nagpurRouteResolve.ts's own docstring. The panel must say WHY next-stop/ETA aren't
 *  showing, not just omit them silently (confirmed live 2026-11-04: 230/250 of
 *  today's board falls into one of these, not the RESOLVED case below). */
const STATUS_LABEL: Record<string, string> = {
  unmapped: 'This route has no stop data loaded yet.',
  depot_mismatch: 'This duty\'s route does not belong to this bus\'s home depot — needs a roster fix.',
  ambiguous_direction: 'This route runs Up and Down and today\'s duty data does not say which — not shown.',
  ambiguous_depot_and_direction: 'Neither depot nor direction is resolvable for this duty — not shown.',
  unparsed_schedule_code: 'No readable schedule for this bus today.',
}

export function NagpurBusDetailPanel({
  vehicle, routes,
}: {
  vehicle: BusBoardRow | null
  routes: BusRouteSummary[]
}): JSX.Element {
  const resolution = useMemo(
    () => (vehicle ? resolveVehicle(vehicle.schedule_code, vehicle.home_depot_id, routes) : null),
    [vehicle, routes],
  )
  const resolvedRouteId = resolution?.status === STATUS_RESOLVED ? resolution.routeId : null

  const [routeDetail, setRouteDetail] = useState<BusRouteDetail | null>(null)
  useEffect(() => {
    if (!resolvedRouteId) {
      setRouteDetail(null)
      return
    }
    let alive = true
    fetchBusRouteDetail(resolvedRouteId)
      .then((d) => { if (alive) setRouteDetail(d) })
      .catch(() => { if (alive) setRouteDetail(null) })
    return () => { alive = false }
  }, [resolvedRouteId])

  const nextStop: BusRouteStop | null = useMemo(() => {
    if (!vehicle || !routeDetail || vehicle.lat == null || vehicle.lon == null) return null
    return nextBusRouteStop(
      routeDetail.stops,
      { lat: vehicle.lat, lon: vehicle.lon },
      { moving: vehicle.speed_kph != null ? vehicle.speed_kph > 3 : undefined },
    )
  }, [vehicle, routeDetail])

  // Last stop in sequence order stands in for "destination" -- a route_id is already
  // one resolved leg (see nagpurRouteResolve.ts's toMbmtStop comment), so its own
  // final stop is the only well-defined endpoint, loop or not.
  const destinationStop: BusRouteStop | null = useMemo(() => {
    if (!routeDetail?.stops.length) return null
    return routeDetail.stops.reduce((a, b) => (b.sequence_no > a.sequence_no ? b : a))
  }, [routeDetail])

  const [eta, setEta] = useState<BusRouteEta | null>(null)
  useEffect(() => {
    setEta(null)
    if (!vehicle || vehicle.lat == null || vehicle.lon == null || !nextStop || !destinationStop) return
    let alive = true
    const originLat = vehicle.lat
    const originLon = vehicle.lon
    const poll = () => {
      fetchBusEta({
        originLat, originLon,
        destLat: destinationStop.lat, destLon: destinationStop.lon,
        waypointLat: nextStop.lat, waypointLon: nextStop.lon,
      })
        .then((e) => { if (alive) setEta(e) })
        .catch(() => { /* a stale/no ETA beats the panel crashing */ })
    }
    poll()
    const id = setInterval(poll, ETA_POLL_MS)
    return () => { alive = false; clearInterval(id) }
  }, [vehicle?.vehicle_id, vehicle?.lat, vehicle?.lon, nextStop?.sequence_no, destinationStop?.sequence_no])

  if (!vehicle) {
    return (
      <Panel className="shrink-0 p-4">
        <EmptyState
          icon={<Radio size={18} />}
          title="Select a bus"
          body="Click a bus on the map or in the Fleet panel to see live route details."
        />
      </Panel>
    )
  }

  return (
    <Panel className="shrink-0 p-4">
      <p className={EYEBROW}>Selected bus</p>
      <h2 className="font-mono text-[18px] font-bold text-[#0F172A]">{vehicle.vehicle_id}</h2>
      <p className="mt-1 text-[12px] text-[#64748B]">
        {vehicle.schedule_code ?? 'no schedule_code'}
        {vehicle.home_depot_id ? ` · ${vehicle.home_depot_id}` : ' · home depot unknown'}
      </p>

      {resolution && resolution.status !== STATUS_RESOLVED && (
        <p className="mt-2 rounded-[10px] bg-[#FFFBEB] px-2.5 py-2 text-[12px] text-[#92400E]">
          {STATUS_LABEL[resolution.status] ?? resolution.status}
        </p>
      )}

      {resolvedRouteId && (
        <div className="mt-2 rounded-[10px] bg-[#F8FAFC] px-2.5 py-2 text-[12px] text-[#0F172A]">
          <p>
            <span className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">Route </span>
            <span className="font-semibold">{routeDetail?.display_name ?? resolvedRouteId}</span>
          </p>
          {routeDetail && (
            <p className="mt-0.5 text-[11px] text-[#64748B]">{routeDetail.stops.length} stops</p>
          )}
          {nextStop && (
            <p className="mt-1">
              <span className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">Next stop </span>
              <span className="font-semibold animate-pulse">{nextStop.stop_name}</span>
            </p>
          )}
          {eta && (eta.next_stop_duration_s != null || eta.destination_duration_s != null) ? (
            <p className="mt-1 font-mono tabular-nums text-[11px] text-[#0369A1]">
              {eta.next_stop_duration_s != null
                ? `ETA next ~${Math.round(eta.next_stop_duration_s / 60)}m`
                : 'ETA next —'}
              {' · '}
              {eta.destination_duration_s != null
                ? `terminus ~${Math.round(eta.destination_duration_s / 60)}m`
                : 'terminus —'}
            </p>
          ) : eta?.error ? (
            <p className="mt-1 text-[11px] text-[#94A3B8]">ETA unavailable</p>
          ) : null}
        </div>
      )}
    </Panel>
  )
}
